
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import GenericTable from '../common/GenericTable';
import { CommonIconAndWithOutIconButton } from '../common/CommonElements';
import { useDispatch, useSelector } from 'react-redux';
import { checkout, fetchCart, removeFromCart, updateCartItem } from '../../redux/cartSlice';
import { useNotification } from '../../context/NotificationContext';
import debounce from 'lodash.debounce';
import { Add, Remove } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Cart = () => {
  const dispatch = useDispatch();
  const { items, loading, error, checkoutStatus } = useSelector((state) => state.cart);
  const [cartItems, setCartItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loadingItems, setLoadingItems] = useState({});
  const [silentLoading, setSilentLoading] = useState(false);
  const addNotification = useNotification();
  const theme = useTheme();
  const navigate = useNavigate();

  const debouncedFetchCart = useCallback(
    debounce(() => {
      setSilentLoading(true); 
      dispatch(fetchCart())
        .unwrap()
        .finally(() => {
          setSilentLoading(false);
        });
    }, 500),
    [dispatch]
  );
  

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  useEffect(() => {
    if (items) {
      setCartItems(items?.data?.cart_items || []);
    }
  }, [items]);

  const columns = [
    {
      label: 'Image',
      field: 'attached_image',
      render: (image, row) => (
        <img 
          src={row?.product?.images?.find((img) => img.image_type==='thumbnail')?.url || 'https://via.placeholder.com/50'} 
          alt='' 
          width={50} 
          height={50} 
        />
      ),
    },
    { 
      label: 'Product Name', 
      field: 'product_name', 
      render: (_, row) => (
        <Typography  
          sx={{ 
            color: theme.palette.text.primary, 
            fontWeight: 400,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={() => navigate(`/product/detail/${row?.product_id}`)}
        >
          {row?.product?.product_name}
        </Typography>
      ) 
    },
    {
      label: 'Unit Price',
      field: 'unitPrice',
      render: (_, row) => `$${parseFloat(row?.price).toFixed(2)}`,
    },
    {
      label: 'Quantity',
      field: 'quantity',
      headerAlign: 'center',
      align: 'center',
      render: (quantity, row) => (
        <Box display="flex" alignItems="center" justifyContent="center">
          <IconButton
            onClick={() => updateQuantity(row, false)}
            disabled={loadingItems[row.id]}
            sx={{ marginRight: '4px', padding: '8px', border: `1px solid ${theme.palette.primary.lighter_gray}`, borderRadius: '50%' }}
          >
            <Remove />
          </IconButton>
          {loadingItems[row.id] ? (
            <Typography sx={{ padding: '0 8px' }}>Updating...</Typography>
          ) : (
            <Typography sx={{ padding: '0 8px' }}>{row?.quantity}</Typography>
          )}
          <IconButton
            onClick={() => updateQuantity(row, true)}
            disabled={loadingItems[row.id]}
            sx={{ marginLeft: '4px', padding: '8px', border: `1px solid ${theme.palette.primary.lighter_gray}`, borderRadius: '50%' }}
          >
            <Add />
          </IconButton>
        </Box>
      ),
    },
    {
      label: 'Total',
      field: 'total',
      render: (_, row) => `$${parseFloat(row?.total).toFixed(2)}`,
    },
  ];

  const actions = [
    {
      icon: (
        <Tooltip title="Remove from Cart" arrow>
          <DeleteIcon />
        </Tooltip>
      ),
      onClick: (row) => removeItem(row),
      style: { backgroundColor: theme.palette.primary.red, minWidth: '40px', padding: '6px' },
    },
  ];

  const updateQuantity = (item, isIncrement) => {
    const currentQuantity = item.quantity;
    const newQuantity = isIncrement ? currentQuantity + 1 : currentQuantity - 1;
  
    if (newQuantity === 0) {
      removeItem(item);
      return;
    }
  
    setLoadingItems((prev) => ({ ...prev, [item.id]: true }));
  
    const updatedCartItems = cartItems.map((cartItem) =>
      cartItem.id === item.id ? { ...cartItem, quantity: newQuantity } : cartItem
    );
    setCartItems(updatedCartItems);
  
    dispatch(updateCartItem({ product_id: item?.product?.id, quantity: newQuantity }))
      .then(() => {
        addNotification('success', 'Quantity updated successfully');
        debouncedFetchCart(); // Fetch updated cart
      })
      .catch((error) => {
        setCartItems(cartItems); // Revert state on failure
        addNotification('error', `Error updating quantity: ${error.message}`);
      })
      .finally(() => {
        setLoadingItems((prev) => {
          const newLoadingItems = { ...prev };
          delete newLoadingItems[item.id];
          return newLoadingItems;
        });
      });
  };
  
  const removeItem = (item) => {
    setLoadingItems((prev) => ({ ...prev, [item.id]: true }));
  
    dispatch(removeFromCart(item.id))
      .then(() => {
        const updatedCartItems = cartItems.filter((cartItem) => cartItem.id !== item.id);
        setCartItems(updatedCartItems);
        addNotification('success', 'Item removed from cart successfully');
        debouncedFetchCart(); // Fetch updated cart
      })
      .catch(() => {
        addNotification('error', 'Error removing item from cart');
      })
      .finally(() => {
        setLoadingItems((prev) => {
          const newLoadingItems = { ...prev };
          delete newLoadingItems[item.id];
          return newLoadingItems;
        });
      });
  };

  const handleSort = (columnKey) => {
    const direction = sortConfig.key === columnKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: columnKey, direction });
    const sortedData = [...cartItems].sort((a, b) => {
      if (a[columnKey] < b[columnKey]) return direction === 'asc' ? -1 : 1;
      if (a[columnKey] > b[columnKey]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setCartItems(sortedData);
  };
  // checkout
  const handleCheckout =async () => {
      const total = calculateTotal().toFixed(2);
      const cartItems = items.data.cart_items || [];
      navigate('/my/checkout', { state: { totalPrice: total, cartItems } });
  }

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  }

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', height: 'auto', flexGrow: 1, gap: 1 }}>
      <Typography
        variant="h4"
        sx={{
          color: theme.palette.heading_color.main,
          fontWeight: 600,
          fontSize: { xs: 20, sm: 24 },
        }}
      >
        Shopping Cart
      </Typography>

      <GenericTable
        columns={columns}
        data={cartItems}
        actions={actions}
        onSort={handleSort}
        sortConfig={sortConfig}
        renderActions={(row) => !row.isSummary}
        loading={!cartItems.length && loading} 
        rowLoading={(row) => !!loadingItems[row.id]} 
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Typography variant="h6" sx={{ mr: 2 }}>
          Total: ${calculateTotal().toFixed(2)}
        </Typography>

        <CommonIconAndWithOutIconButton
          text="Checkout"
          bgColor={theme.palette.secondary.main}
          color={theme.palette.tertiary.main}
          fontSize="14px"
          px="20px"
          py="8px"
          action={handleCheckout}
        />
      </Box>
    </Box>
  );
};

export default Cart;
